import React, { useState } from 'react';
import { addMission, suggestMission } from '../api/gameBuilderAPI';

/**
 * Adding a mission to a saved plan (backend planner.add_mission / suggest_mission): for one of the plan's tasks, or with
 * a NEW task - a part of the document the plan left out. "Suggest with AI" (one small call) fills the form from the
 * task, or from the document for the part named; the creator edits it and adds it. The new mission is then designed,
 * built and shared like the others.
 */
const NEW = 'new';
const blankTask = { title: '', rules: '', mistakes: '' };

const lines = (text) => String(text || '').split('\n').map((x) => x.trim()).filter(Boolean);

export default function AddMission({ plan, onAdded }) {
  const [open, setOpen] = useState(false);
  const [taskId, setTaskId] = useState(plan.tasks?.[0]?.id || NEW);
  const [task, setTask] = useState(blankTask);
  const [mission, setMission] = useState({ name: '', objective: '', scenario: '' });
  const [traps, setTraps] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const isNew = taskId === NEW;
  const chosen = plan.tasks?.find((t) => t.id === taskId);
  const mistakes = isNew ? lines(task.mistakes) : (chosen?.mistakes || []).map((m) => m.mistake);

  const reset = () => {
    setOpen(false); setTask(blankTask); setMission({ name: '', objective: '', scenario: '' }); setTraps([]); setError('');
  };
  const run = async (label, fn) => {
    setBusy(label); setError('');
    try { return await fn(); } catch (e) { setError(e.message); return null; } finally { setBusy(''); }
  };

  const suggest = async () => {
    const out = await run('Suggesting a mission… (one small AI call)', () => suggestMission(plan.id, isNew ? null : taskId, isNew ? task.title : ''));
    if (!out) return;
    if (out.task) {
      setTask({ title: out.task.title, rules: out.task.rules.join('\n'), mistakes: out.task.mistakes.map((m) => m.mistake).join('\n') });
    }
    setMission({ name: out.mission.name, objective: out.mission.objective, scenario: out.mission.scenario });
    setTraps(out.mission.traps || []);
  };

  const add = async () => {
    const newTask = isNew ? { title: task.title, rules: lines(task.rules), mistakes: lines(task.mistakes).map((m) => ({ mistake: m })) } : null;
    const out = await run('Adding…', () => addMission(plan.id, { ...mission, task: isNew ? null : taskId, traps }, newTask));
    if (out) { onAdded(out.plan, out.mission); reset(); }
  };

  if (!open) {
    return <button type="button" className="gb-btn gb-btn-quiet" onClick={() => setOpen(true)}>＋ Add a mission</button>;
  }
  return (
    <section className="gb-card gb-add-mission" aria-label="Add a mission">
      <h3>Add a mission</h3>
      <label className="gb-field">For which task
        <select value={taskId} onChange={(e) => { setTaskId(e.target.value); setTraps([]); }} disabled={!!busy}>
          {(plan.tasks || []).map((t) => <option key={t.id} value={t.id}>{t.id} · {t.title}</option>)}
          <option value={NEW}>A new task - a part of the document the plan left out</option>
        </select>
      </label>
      {isNew && (
        <>
          <label className="gb-field">Task (the part of the document)
            <input value={task.title} maxLength={80} placeholder="Cooking and hot holding" disabled={!!busy}
              onChange={(e) => setTask((t) => ({ ...t, title: e.target.value }))} />
          </label>
          <label className="gb-field">Its rules (one per line)
            <textarea rows={3} value={task.rules} disabled={!!busy} onChange={(e) => setTask((t) => ({ ...t, rules: e.target.value }))} />
          </label>
          <label className="gb-field">Mistakes people make (one per line)
            <textarea rows={2} value={task.mistakes} disabled={!!busy} onChange={(e) => setTask((t) => ({ ...t, mistakes: e.target.value }))} />
          </label>
        </>
      )}
      <div className="gb-actions">
        <button type="button" className="gb-btn gb-btn-quiet" disabled={!!busy || (isNew && !task.title.trim())} onClick={suggest}>
          ✨ Suggest it with AI
        </button>
        <span className="gb-small gb-muted">{isNew ? 'Name the part of the document first - the AI reads it there.' : 'One small AI call - edit what it writes.'}</span>
      </div>
      <label className="gb-field">Mission name
        <input value={mission.name} maxLength={80} disabled={!!busy} onChange={(e) => setMission((m) => ({ ...m, name: e.target.value }))} />
      </label>
      <label className="gb-field">Objective
        <input value={mission.objective} maxLength={160} disabled={!!busy} onChange={(e) => setMission((m) => ({ ...m, objective: e.target.value }))} />
      </label>
      <label className="gb-field">What the player walks into
        <textarea rows={2} value={mission.scenario} disabled={!!busy} onChange={(e) => setMission((m) => ({ ...m, scenario: e.target.value }))} />
      </label>
      {mistakes.length > 0 && (
        <fieldset className="gb-field gb-add-traps">
          <legend>Mistakes this mission tempts</legend>
          {mistakes.map((m) => (
            <label key={m} className="gb-small">
              <input type="checkbox" checked={traps.includes(m)} disabled={!!busy}
                onChange={() => setTraps((t) => (t.includes(m) ? t.filter((x) => x !== m) : [...t, m]))} /> {m}
            </label>
          ))}
        </fieldset>
      )}
      <div className="gb-actions">
        <button type="button" className="gb-btn" disabled={!!busy || !mission.name.trim() || !mission.scenario.trim()} onClick={add}>Add the mission</button>
        <button type="button" className="gb-btn gb-btn-quiet" disabled={!!busy} onClick={reset}>Cancel</button>
      </div>
      {busy && <p className="gb-progress"><span className="gb-spinner" /> {busy}</p>}
      {error && <div className="gb-error" role="alert">{error}</div>}
    </section>
  );
}
